import { storeToRefs } from "pinia";
import { type ComputedRef, computed } from "vue";

import {
    type Resource,
    type ResourceActions,
    type ResourceHavingAction,
    resourceActions,
} from "@/services/PermissionsService";
import { useAuthStore } from "@/stores/authStore";

type UseAccess<R extends Resource> = {
    permits<A extends ResourceActions<R>>(action: A): boolean;
} & ((R extends ResourceHavingAction<"read"> ? { readable: ComputedRef<boolean> } : object) &
    (R extends ResourceHavingAction<"edit"> ? { editable: ComputedRef<boolean> } : object) &
    (R extends ResourceHavingAction<"delete"> ? { deletable: ComputedRef<boolean> } : object));

export function useAccess<R extends Resource>(resource: R): UseAccess<R> {
    const { permissions } = storeToRefs(useAuthStore());

    const obj = {
        permits<A extends ResourceActions<R>>(action: A): boolean {
            return permissions.value?.[resource]?.includes(action) ?? false;
        },
    } as UseAccess<R>;

    const actions = resourceActions(resource);
    if (actions.includes("read")) {
        (obj as Record<string, unknown>).readable = computed(() => obj.permits("read" as ResourceActions<R>));
    }
    if (actions.includes("edit")) {
        (obj as Record<string, unknown>).editable = computed(() => obj.permits("edit" as ResourceActions<R>));
    }
    if (actions.includes("delete")) {
        (obj as Record<string, unknown>).deletable = computed(() => obj.permits("delete" as ResourceActions<R>));
    }

    return obj;
}
